import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import type { Claim, ClaimStatus, Runner } from '../types';
import { db, existingDb } from './firebase';

/** runners のゼッケン存在確認 */
export async function checkBibExists(
  raceId: string,
  bib: string
): Promise<{ exists: boolean; hasVerificationData: boolean; hasFinishData: boolean }> {
  const runnersRef = collection(db, 'runners');
  const q = query(runnersRef, where('raceId', '==', raceId), where('bib', '==', bib), limit(1));

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return { exists: false, hasVerificationData: false, hasFinishData: false };
  }

  const runner = snapshot.docs[0].data();
  const hasVerificationData = Boolean(runner.birth && runner.email);
  // timing 配列に locationId=11（フィニッシュ地点）があるか
  const timing = runner.timing as Array<{ locationId: string; time: string }> | undefined;
  const hasFinishData = timing?.some((t) => t.locationId === '11') ?? false;

  return { exists: true, hasVerificationData, hasFinishData };
}

/** runners で本人確認（メール + 生年月日） */
export async function verifyIdentity(
  raceId: string,
  email: string,
  birthday: string
): Promise<{ success: boolean; bib?: string; fullName?: string; error?: string }> {
  // birthday: YYYY-MM-DD → YYYY/MM/DD（Firestoreの形式）
  const formattedBirthday = birthday.replace(/-/g, '/');

  const runnersRef = collection(db, 'runners');
  const q = query(
    runnersRef,
    where('raceId', '==', raceId),
    where('email', '==', email),
    where('birth', '==', formattedBirthday)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return { success: false, error: '入力された情報が一致しません' };
  }

  const runner = snapshot.docs[0].data();
  return {
    success: true,
    bib: String(runner.bib),
    fullName: runner.fullName,
  };
}

/** timingHash から runner を取得 */
export async function getRunnerByTimingHash(timingHash: string): Promise<Runner | null> {
  const runnersRef = collection(db, 'runners');
  const q = query(runnersRef, where('timingHash', '==', timingHash), limit(1));

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    ...docData.data(),
  } as Runner;
}

/** bib から runner を取得 */
export async function getRunnerByBib(raceId: string, bib: string): Promise<Runner | null> {
  const runnersRef = collection(db, 'runners');
  const q = query(runnersRef, where('raceId', '==', raceId), where('bib', '==', bib), limit(1));

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    ...docData.data(),
  } as Runner;
}

/** raceAlias + bib から runner を取得 */
export async function getRunnerByAliasBib(raceAlias: string, bib: string): Promise<Runner | null> {
  const runnersRef = collection(db, 'runners');
  const q = query(runnersRef, where('raceAlias', '==', raceAlias), where('bib', '==', bib), limit(1));

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    ...docData.data(),
  } as Runner;
}

/** 既存のclaimを取得（uid + raceId でクエリ） */
export async function getExistingClaim(uid: string, raceId: string): Promise<Claim | null> {
  const claimsRef = collection(db, 'claims');
  const q = query(
    claimsRef,
    where('uid', '==', uid),
    where('raceId', '==', raceId),
    where('deletedAt', '==', null),
    limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    ...docData.data(),
  } as Claim;
}

/** runnerId で claim を直接取得 */
export async function getClaimByRunnerId(runnerId: string): Promise<Claim | null> {
  const claimRef = doc(db, 'claims', runnerId);
  const claimSnap = await getDoc(claimRef);

  if (!claimSnap.exists()) {
    return null;
  }

  return {
    id: claimSnap.id,
    ...claimSnap.data(),
  } as Claim;
}

/** claim のステータス取得 */
export async function getClaimStatus(claimId: string): Promise<{ status: ClaimStatus; claim: Claim } | null> {
  const claimRef = doc(db, 'claims', claimId);
  const claimDoc = await getDoc(claimRef);

  if (!claimDoc.exists()) {
    return null;
  }

  const claim = {
    id: claimDoc.id,
    ...claimDoc.data(),
  } as Claim;

  return { status: claim.status, claim };
}

/** Firestoreからraceを取得 */
export async function getRaceFromFirestore(
  raceAlias: string
): Promise<{ id: string; data: Record<string, unknown> } | null> {
  const racesRef = collection(db, 'races');
  const q = query(racesRef, where('alias', '==', raceAlias), where('deletedAt', '==', null), limit(1));

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    data: docData.data(),
  };
}

/** ユーザー情報の型 */
export interface UserData {
  walletAddress?: string;
  twitterCredential?: {
    accessToken: string;
    secret: string;
  };
}

/** ユーザー情報を取得 - users は dot99 にあるため existingDb を使用 */
export async function getUserData(uid: string): Promise<UserData | null> {
  const userRef = doc(existingDb, 'users', uid);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    return null;
  }

  return userDoc.data() as UserData;
}

interface CreateClaimParams {
  uid: string;
  raceId: string;
  raceAlias: string;
  raceName: string;
  raceDate: string;
  bib: string;
  startTime: string;
  time: string;
  netTime: string;
  runnerId: string; // runners の docId（${raceAlias}-${bib}）
  mintContext: 'on_site' | 'online';
  destination: string | null;
  chain: string;
  contractAddress: string;
  tokenName: string;
}

/** claims ドキュメント作成（ドキュメントID = runnerId） */
export async function createClaim(params: CreateClaimParams): Promise<string> {
  const docRef = doc(db, 'claims', params.runnerId);

  await setDoc(docRef, {
    bib: params.bib,
    chain: params.chain,
    completedAt: null,
    contractAddress: params.contractAddress,
    createdAt: serverTimestamp(),
    deletedAt: null,
    destination: params.destination,
    failedAt: null,
    fileName: null,
    hasIntegrated: false,
    mediaUri: null,
    mint_context: params.mintContext,
    netTime: params.netTime,
    raceAlias: params.raceAlias,
    raceDate: params.raceDate,
    raceId: params.raceId,
    raceName: params.raceName,
    randomSeed: Math.random(),
    sourceFileName: null,
    startTime: params.startTime,
    startedAt: null,
    status: 'ready',
    time: params.time,
    tokenId: null,
    tokenName: params.tokenName,
    txFee: null,
    txHash: null,
    uid: params.uid,
    updatedAt: serverTimestamp(),
  });

  return params.runnerId;
}

/**
 * ユーザーの完了済みclaim一覧を取得（ギャラリー用）
 * 全年度表示のため、3つのコレクションをクロス参照:
 * - heraldingera の claims（2026〜）
 * - dot99 の marathonClaims（2025）
 * - dot99 の claims（2024）
 */
export async function getCompletedClaims(uid: string): Promise<Claim[]> {
  // 1. heraldingera の claims（2026〜）
  const newClaimsRef = collection(db, 'claims');
  const newClaimsQuery = query(
    newClaimsRef,
    where('uid', '==', uid),
    where('deletedAt', '==', null),
    orderBy('completedAt', 'desc')
  );

  // 2. dot99 の marathonClaims（2025）
  const existingMarathonClaimsRef = collection(existingDb, 'marathonClaims');
  const existingMarathonClaimsQuery = query(
    existingMarathonClaimsRef,
    where('uid', '==', uid),
    where('deletedAt', '==', null),
    orderBy('completedAt', 'desc')
  );

  // 3. dot99 の claims（2024）- type='marathon' でフィルタ
  const existingClaimsRef = collection(existingDb, 'claims');
  const existingClaimsQuery = query(
    existingClaimsRef,
    where('uid', '==', uid),
    where('type', '==', 'marathon'),
    where('deletedAt', '==', null),
    orderBy('claimedAt', 'desc')
  );

  // 並列で取得
  const [newSnapshot, existingMarathonSnapshot, existingClaimsSnapshot] = await Promise.all([
    getDocs(newClaimsQuery),
    getDocs(existingMarathonClaimsQuery),
    getDocs(existingClaimsQuery),
  ]);

  const claims: Claim[] = [];

  // heraldingera の claims
  for (const docData of newSnapshot.docs) {
    const data = docData.data();
    if (data.status === 'completed') {
      claims.push({ id: docData.id, ...data } as Claim);
    }
  }

  // dot99 の marathonClaims
  for (const docData of existingMarathonSnapshot.docs) {
    const data = docData.data();
    if (data.status === 'completed') {
      claims.push({ id: docData.id, ...data } as Claim);
    }
  }

  // dot99 の claims（2024）- claimedAt があれば完了済み
  for (const docData of existingClaimsSnapshot.docs) {
    const data = docData.data();
    if (data.claimedAt) {
      claims.push({ id: docData.id, ...data } as Claim);
    }
  }

  return claims;
}

/**
 * ユーザーの全claim履歴を取得（履歴画面用）
 * 2026〜のみ表示（過去大会の履歴は表示しない仕様）
 */
export async function getClaimHistory(uid: string): Promise<Claim[]> {
  const claimsRef = collection(db, 'claims');
  const q = query(claimsRef, where('uid', '==', uid), where('deletedAt', '==', null), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as Claim
  );
}
